/**
 * RendezvousMessage encode/decode.
 *
 * Field numbers are protocol and must match rendezvous.proto exactly; they are
 * listed beside each field so a future proto change is easy to reconcile.
 */
import { Writer, eachField } from "./wire.js";
export const ConnType = {
    DEFAULT: 0,
    FILE_TRANSFER: 1,
    PORT_FORWARD: 2,
    RDP: 3,
    VIEW_CAMERA: 4,
    TERMINAL: 5,
};
export const NatType = { UNKNOWN: 0, ASYMMETRIC: 1, SYMMETRIC: 2 };
/** RendezvousMessage.union field numbers. */
export const RZ = {
    register_peer: 6,
    register_peer_response: 7,
    punch_hole_request: 8,
    punch_hole: 9,
    punch_hole_sent: 10,
    punch_hole_response: 11,
    fetch_local_addr: 12,
    local_addr: 13,
    configure_update: 14,
    register_pk: 15,
    register_pk_response: 16,
    software_update: 17,
    request_relay: 18,
    relay_response: 19,
    test_nat_request: 20,
    test_nat_response: 21,
    peer_discovery: 22,
    online_request: 23,
    online_response: 24,
    key_exchange: 25,
    hc: 26,
};
export function encodePunchHoleRequest(p) {
    const inner = new Writer()
        .string(1, p.id)
        .int32(2, p.natType)
        .string(3, p.licenceKey)
        .int32(4, p.connType)
        .string(5, p.token)
        .string(6, p.version)
        // udp_port = 7 stays 0: a browser cannot receive UDP.
        .bool(8, p.forceRelay)
        .finish();
    return new Writer().message(RZ.punch_hole_request, inner).finish();
}
export function encodeRequestRelay(p) {
    const inner = new Writer()
        .string(1, p.id)
        .string(2, p.uuid)
        // socket_addr = 3 is filled in by hbbs.
        .string(4, "")
        .bool(5, p.secure)
        .string(6, p.licenceKey)
        .int32(7, p.connType)
        .string(8, p.token)
        .finish();
    return new Writer().message(RZ.request_relay, inner).finish();
}
export function encodeTestNatRequest(serial = 0) {
    const inner = new Writer().int32(1, serial).finish();
    return new Writer().message(RZ.test_nat_request, inner).finish();
}
export function encodeOnlineRequest(id, peers) {
    const w = new Writer().string(1, id);
    for (const p of peers)
        w.string(2, p);
    return new Writer().message(RZ.online_request, w.finish()).finish();
}
/**
 * Sent over UDP to claim an ID and publish the device's signing key.
 * Browsers never send this; it exists for the test harness and for tooling.
 */
export function encodeRegisterPk(p) {
    const inner = new Writer()
        .string(1, p.id)
        .bytes(2, p.uuid)
        .bytes(3, p.pk)
        .string(4, p.oldId)
        .finish();
    return new Writer().message(RZ.register_pk, inner).finish();
}
export function encodeRegisterPeer(id, serial = 0) {
    const inner = new Writer().string(1, id).int32(2, serial).finish();
    return new Writer().message(RZ.register_peer, inner).finish();
}
/** As decodeMessage: the first recognised union member wins. */
export function decodeRendezvous(buf) {
    let out = { kind: "unknown", field: 0 };
    eachField(buf, (field, type, r) => {
        if (type !== 2 /* WireType.Bytes */)
            return;
        switch (field) {
            case RZ.punch_hole_response:
                out = { kind: "punch_hole_response", value: parsePunchHoleResponse(r.bytes()) };
                return true;
            case RZ.relay_response:
                out = { kind: "relay_response", value: parseRelayResponse(r.bytes()) };
                return true;
            case RZ.test_nat_response:
                out = { kind: "test_nat_response", value: parseTestNatResponse(r.bytes()) };
                return true;
            case RZ.online_response:
                out = { kind: "online_response", value: parseOnlineResponse(r.bytes()) };
                return true;
            case RZ.register_peer_response:
                out = { kind: "register_peer_response", value: parseRegisterPeerResponse(r.bytes()) };
                return true;
            case RZ.register_pk_response:
                out = { kind: "register_pk_response", value: parseRegisterPkResponse(r.bytes()) };
                return true;
            default:
                if (out.kind === "unknown")
                    out = { kind: "unknown", field };
                return;
        }
    });
    return out;
}
function parsePunchHoleResponse(buf) {
    const v = {
        socketAddr: new Uint8Array(),
        pk: new Uint8Array(),
        relayServer: "",
        otherFailure: "",
    };
    eachField(buf, (f, _t, r) => {
        switch (f) {
            case 1:
                v.socketAddr = r.bytes().slice();
                return true;
            case 2:
                v.pk = r.bytes().slice();
                return true;
            case 3:
                v.failure = r.int32();
                return true;
            case 4:
                v.relayServer = r.string();
                return true;
            case 5:
                v.natType = r.int32();
                return true;
            case 6:
                v.isLocal = r.bool();
                return true;
            case 7:
                v.otherFailure = r.string();
                return true;
            case 9:
                v.isUdp = r.bool();
                return true;
            default: return;
        }
    });
    return v;
}
function parseRelayResponse(buf) {
    const v = {
        socketAddr: new Uint8Array(), uuid: "", relayServer: "", refuseReason: "", version: "",
    };
    eachField(buf, (f, _t, r) => {
        switch (f) {
            case 1:
                v.socketAddr = r.bytes().slice();
                return true;
            case 2:
                v.uuid = r.string();
                return true;
            case 3:
                v.relayServer = r.string();
                return true;
            case 4:
                v.id = r.string();
                return true;
            case 5:
                v.pk = r.bytes().slice();
                return true;
            case 6:
                v.refuseReason = r.string();
                return true;
            case 7:
                v.version = r.string();
                return true;
            default: return;
        }
    });
    return v;
}
function parseTestNatResponse(buf) {
    let port = 0;
    eachField(buf, (f, _t, r) => {
        if (f === 1) {
            port = r.int32();
            return true;
        }
        return;
    });
    return { port };
}
function parseOnlineResponse(buf) {
    let states = new Uint8Array();
    eachField(buf, (f, _t, r) => {
        if (f === 1) {
            states = r.bytes().slice();
            return true;
        }
        return;
    });
    return { states };
}
/** RegisterPkResponse.Result: 0 OK, 2 UUID_MISMATCH, 3 ID_EXISTS, 4 TOO_FREQUENT, ... */
function parseRegisterPkResponse(buf) {
    let result = 0;
    let keepAlive = 0;
    eachField(buf, (f, _t, r) => {
        if (f === 1) {
            result = r.int32();
            return true;
        }
        if (f === 2) {
            keepAlive = r.int32();
            return true;
        }
        return;
    });
    return { result, keepAlive };
}
function parseRegisterPeerResponse(buf) {
    let requestPk = false;
    eachField(buf, (f, _t, r) => {
        if (f === 2) {
            requestPk = r.bool();
            return true;
        }
        return;
    });
    return { requestPk };
}
/** Unpacks the bitset returned by OnlineResponse (MSB-first per byte). */
export function decodeOnlineStates(states, count) {
    const out = [];
    for (let i = 0; i < count; i++) {
        const byte = states[i >> 3] ?? 0;
        out.push((byte & (0x01 << (7 - (i % 8)))) !== 0);
    }
    return out;
}
export function parseIdPk(buf) {
    const v = { id: "", pk: new Uint8Array() };
    eachField(buf, (f, _t, r) => {
        switch (f) {
            case 1:
                v.id = r.string();
                return true;
            case 2:
                v.pk = r.bytes().slice();
                return true;
            // field 3 (dtls_fingerprint) exists on newer clients; not needed here.
            default: return;
        }
    });
    return v;
}
